import type { Card, CardLanguage, CardSet } from "../types";

export interface CardQuery {
  name?: string;
  /** Collector number as printed, e.g. `25`, `025` or `TG05`. Leading zeros don't matter. */
  number?: string;
  /** Printed set code, e.g. `PAL`, `SV2a`, `CSV4C`. */
  setCode?: string;
  /** Exact provider set id, e.g. `sv02` (a set picked from `getSets`). Wins over `setCode`. */
  setId?: string;
  /** The total after the slash in `025/165`. Narrows a number-only search. */
  printedTotal?: number;
  language: CardLanguage;
  page?: number;
}

/**
 * SPECS §8. Search results may be partial cards (no rarity or prices); `getCard` is complete.
 * A set (code or id) alone lists the whole set; a name with a set or number filters by name.
 */
export interface CardProvider {
  search(q: CardQuery): Promise<Card[]>;
  /** `fresh` skips any cache, for a price refresh (FR-22). */
  getCard(id: string, language: CardLanguage, options?: { fresh?: boolean }): Promise<Card>;
  getSets(language: CardLanguage): Promise<CardSet[]>;
}

export type ProviderErrorKind = "not-found" | "rate-limit" | "network" | "server";

export class ProviderError extends Error {
  constructor(
    readonly kind: ProviderErrorKind,
    message: string = kind,
  ) {
    super(message);
    this.name = "ProviderError";
  }
}

/** `025`, `25` and `0025` are the same card number; `TG05` and `TG5` too. */
export function normaliseNumber(number: string): string {
  const match = /^([A-Za-z-]*)0*(\d+)([A-Za-z]*)$/.exec(number.trim());
  if (!match) return number.trim().toUpperCase();
  return `${match[1].toUpperCase()}${match[2]}${match[3].toUpperCase()}`;
}
